import React, { useState, useEffect, useMemo } from 'react';
import { backupAPI } from '../../../utils/api';
import Swal from 'sweetalert2';
import { 
  FaDatabase, FaDownload, FaTrashAlt, FaSyncAlt, 
  FaClock, FaFileArchive, FaCheckCircle, FaExclamationTriangle,
  FaServer, FaCloudDownloadAlt, FaHdd
} from 'react-icons/fa';

export default function BackupsPage() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const loadBackups = async () => {
    setLoading(true);
    try {
      const res = await backupAPI.getAll();
      setBackups(res.data.content || res.data || []);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error loading backups', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBackups();
  }, []);

  const handleTriggerBackup = async () => {
    setActionLoading(true);
    Swal.fire({
      title: 'Generating Database Backup...',
      text: 'Running pg_dump to extract database structure and data. Please wait.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      await backupAPI.generate();
      await loadBackups();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Database backup has been successfully generated.',
        timer: 3000,
        toast: true,
        position: 'top-end',
        showConfirmButton: false
      });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Backup Failed',
        text: e.response?.data?.error || e.message
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBackup = async (filename) => {
    const result = await Swal.fire({
      title: 'Delete Backup?',
      text: `Are you sure you want to permanently delete the backup "${filename}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Delete Backup',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await backupAPI.delete(filename);
      await loadBackups();
      Swal.fire({
        icon: 'success',
        text: 'Backup successfully deleted.',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false
      });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: e.response?.data?.error || e.message
      });
    }
  };

  const handleDownloadBackup = (filename) => {
    // Navigate directly to download route
    const downloadUrl = backupAPI.downloadUrl(filename);
    window.open(downloadUrl, '_blank');
  };

  // Helper to format byte sizes
  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  // Stats calculation
  const stats = useMemo(() => {
    const total = backups.length;
    const totalSize = backups.reduce((acc, curr) => acc + (curr.sizeBytes || 0), 0);
    const lastBackup = backups.length > 0 ? backups[0].createdAt : null;
    return { total, totalSize, lastBackup };
  }, [backups]);

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      {/* Header section */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '28px',
        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        paddingBottom: '20px'
      }}>
        <div>
          <h1 style={{ 
            fontSize: '1.8rem', 
            fontWeight: 800, 
            color: '#1e293b', 
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaDatabase style={{ color: '#0f6fad' }} /> Database Backups
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.95rem', marginTop: '6px', marginBottom: 0 }}>
            Secure administrative console to manage, download, and trigger database physical SQL backups.
          </p>
        </div>
        <div>
          <button
            onClick={handleTriggerBackup}
            disabled={actionLoading}
            style={{
              background: 'linear-gradient(135deg, #0f6fad 0%, #1e40af 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '12px 22px',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.92rem',
              cursor: actionLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 4px 12px rgba(15, 111, 173, 0.25)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(15, 111, 173, 0.35)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 111, 173, 0.25)';
            }}
          >
            <FaSyncAlt className={actionLoading ? 'fa-spin' : ''} /> Generate Live Backup
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '20px', 
        marginBottom: '32px' 
      }}>
        {/* Card 1: Total Backups */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
          border: '1px solid rgba(226, 232, 240, 0.6)',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'rgba(15, 111, 173, 0.08)',
            color: '#0f6fad',
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem'
          }}>
            <FaFileArchive />
          </div>
          <div>
            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Backups Archived
            </span>
            <h3 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1e293b', margin: '4px 0 0 0' }}>
              {stats.total} Files
            </h3>
          </div>
          <div style={{
            position: 'absolute',
            right: '-10px',
            bottom: '-15px',
            fontSize: '5rem',
            opacity: 0.03,
            color: '#0f6fad',
            pointerEvents: 'none'
          }}>
            <FaFileArchive />
          </div>
        </div>

        {/* Card 2: Total Storage Volume */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
          border: '1px solid rgba(226, 232, 240, 0.6)',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'rgba(16, 172, 132, 0.08)',
            color: '#10ac84',
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem'
          }}>
            <FaHdd />
          </div>
          <div>
            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Storage Volume
            </span>
            <h3 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1e293b', margin: '4px 0 0 0' }}>
              {formatBytes(stats.totalSize)}
            </h3>
          </div>
          <div style={{
            position: 'absolute',
            right: '-10px',
            bottom: '-15px',
            fontSize: '5rem',
            opacity: 0.03,
            color: '#10ac84',
            pointerEvents: 'none'
          }}>
            <FaHdd />
          </div>
        </div>

        {/* Card 3: Scheduler Info */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
          border: '1px solid rgba(226, 232, 240, 0.6)',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'rgba(243, 156, 18, 0.08)',
            color: '#f39c12',
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem'
          }}>
            <FaClock />
          </div>
          <div>
            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Backup Automation
            </span>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#1e293b', margin: '6px 0 0 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaCheckCircle style={{ color: '#10ac84', fontSize: '0.95rem' }} /> Daily pg_dump Active
            </h3>
            <span style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginTop: '2px' }}>
              7-Day Retention Cleanup Policy
            </span>
          </div>
          <div style={{
            position: 'absolute',
            right: '-10px',
            bottom: '-15px',
            fontSize: '5rem',
            opacity: 0.03,
            color: '#f39c12',
            pointerEvents: 'none'
          }}>
            <FaClock />
          </div>
        </div>
      </div>

      {/* Warning Box */}
      <div style={{
        background: 'rgba(245, 158, 11, 0.06)',
        border: '1px dashed rgba(245, 158, 11, 0.3)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '32px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px'
      }}>
        <FaExclamationTriangle style={{ color: '#d97706', fontSize: '1.2rem', marginTop: '3px', flexShrink: 0 }} />
        <div>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '0.92rem', fontWeight: 700, color: '#d97706' }}>
            System Administration Notice
          </h4>
          <p style={{ margin: 0, fontSize: '0.86rem', color: '#b45309', lineHeight: '1.5' }}>
            Database backups contain sensitive ERP operational data including roles, user credentials, finance data, and institutional configurations. Please safeguard downloaded SQL dump files and ensure they are stored in accredited, encrypted vaults only.
          </p>
        </div>
      </div>

      {/* Backups List Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
        border: '1px solid rgba(226, 232, 240, 0.6)',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '20px 24px', 
          borderBottom: '1px solid rgba(226, 232, 240, 0.6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 750, color: '#1e293b', margin: 0 }}>
            Stored SQL Backups ({backups.length})
          </h3>
          <button 
            onClick={loadBackups}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.85rem',
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: '6px',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(226, 232, 240, 0.4)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <FaSyncAlt /> Refresh List
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8' }}>
            <FaSyncAlt className="fa-spin" style={{ fontSize: '2rem', color: '#0f6fad', marginBottom: '12px' }} />
            <p style={{ margin: 0, fontWeight: 500 }}>Scanning backups catalog...</p>
          </div>
        ) : backups.length === 0 ? (
          <div style={{ padding: '80px 0', textAlign: 'center', color: '#94a3b8' }}>
            <FaServer style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '16px' }} />
            <h4 style={{ margin: '0 0 6px 0', color: '#475569', fontWeight: 700 }}>No Backup Files Found</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
              No database backups have been generated yet. Click the &quot;Generate Live Backup&quot; button above to trigger pg_dump.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid rgba(226, 232, 240, 0.6)' }}>
                  <th style={{ padding: '16px 24px', fontSize: '0.82rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>File Details</th>
                  <th style={{ padding: '16px 24px', fontSize: '0.82rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Storage Size</th>
                  <th style={{ padding: '16px 24px', fontSize: '0.82rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Created Date & Time</th>
                  <th style={{ padding: '16px 24px', fontSize: '0.82rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b, idx) => (
                  <tr 
                    key={b.filename} 
                    style={{ 
                      borderBottom: idx < backups.length - 1 ? '1px solid rgba(226, 232, 240, 0.5)' : 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(248, 250, 252, 0.5)'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* Column 1: File Details */}
                    <td style={{ padding: '18px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{
                          background: b.filename.startsWith('auto') ? 'rgba(243, 156, 18, 0.08)' : 'rgba(15, 111, 173, 0.08)',
                          color: b.filename.startsWith('auto') ? '#f39c12' : '#0f6fad',
                          padding: '10px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <FaDatabase />
                        </div>
                        <div>
                          <span style={{
                            display: 'block',
                            fontWeight: 700,
                            color: '#1e293b',
                            fontSize: '0.92rem',
                            wordBreak: 'break-all'
                          }}>
                            {b.filename}
                          </span>
                          <span style={{
                            display: 'inline-block',
                            marginTop: '4px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            background: b.filename.startsWith('auto') ? 'rgba(243, 156, 18, 0.1)' : 'rgba(15, 111, 173, 0.1)',
                            color: b.filename.startsWith('auto') ? '#d97706' : '#0f6fad'
                          }}>
                            {b.filename.startsWith('auto') ? 'Scheduled Backup' : 'Manual Dump'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Column 2: Storage Size */}
                    <td style={{ padding: '18px 24px' }}>
                      <span style={{
                        fontWeight: 700,
                        color: '#334155',
                        fontSize: '0.92rem',
                        background: '#f1f5f9',
                        padding: '4px 10px',
                        borderRadius: '6px'
                      }}>
                        {formatBytes(b.sizeBytes)}
                      </span>
                    </td>

                    {/* Column 3: Creation Date */}
                    <td style={{ padding: '18px 24px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>
                          {new Date(b.createdAt).toLocaleDateString(undefined, { 
                            weekday: 'short', 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                          {new Date(b.createdAt).toLocaleTimeString(undefined, { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </span>
                      </div>
                    </td>

                    {/* Column 4: Action Controls */}
                    <td style={{ padding: '18px 24px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button
                          onClick={() => handleDownloadBackup(b.filename)}
                          title="Download SQL File"
                          style={{
                            background: 'rgba(15, 111, 173, 0.08)',
                            color: '#0f6fad',
                            border: 'none',
                            padding: '10px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            transition: 'background 0.2s, color 0.2s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = '#0f6fad';
                            e.currentTarget.style.color = '#ffffff';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(15, 111, 173, 0.08)';
                            e.currentTarget.style.color = '#0f6fad';
                          }}
                        >
                          <FaCloudDownloadAlt />
                        </button>
                        <button
                          onClick={() => handleDeleteBackup(b.filename)}
                          title="Delete Backup File"
                          style={{
                            background: 'rgba(231, 76, 60, 0.08)',
                            color: '#e74c3c',
                            border: 'none',
                            padding: '10px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            transition: 'background 0.2s, color 0.2s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = '#e74c3c';
                            e.currentTarget.style.color = '#ffffff';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(231, 76, 60, 0.08)';
                            e.currentTarget.style.color = '#e74c3c';
                          }}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
